import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { OPEN_MARKETS_SHORTCUT } from '@/feature/market_info/market_select/marketMenuShortcut.ts';
import { IS_MAC, isShortcut, type ShortcutKeys } from '@/utils/shortcutKey';

export type GeneralShortcutId =
  | 'openMarkets'
  | 'long'
  | 'short'
  | 'toggleMarketLimit'
  | 'closePosition'
  | 'openSettings'
  | 'deposit'
  | 'withdraw';

export interface ShortcutDefinition extends ShortcutKeys {
  id: GeneralShortcutId;
  label: MessageDescriptor;
  /** Acts on trade-page UI (market menu, order form); ignored on other pages. */
  tradeOnly?: boolean;
}

/** Bound by useGeneralShortcuts; the Keyboard Shortcuts dialog lists them in this order. */
export const GENERAL_SHORTCUTS: readonly ShortcutDefinition[] = [
  {
    id: 'openMarkets',
    ...OPEN_MARKETS_SHORTCUT,
    tradeOnly: true,
    label: msg({ comment: 'Keyboard shortcut action', message: 'Open Markets' }),
  },
  {
    id: 'long',
    key: 'l',
    tradeOnly: true,
    label: msg({ comment: 'Keyboard shortcut action: switch the order form to Long', message: 'Long' }),
  },
  {
    id: 'short',
    key: 's',
    tradeOnly: true,
    label: msg({ comment: 'Keyboard shortcut action: switch the order form to Short', message: 'Short' }),
  },
  {
    id: 'toggleMarketLimit',
    key: 'o',
    tradeOnly: true,
    label: msg({
      comment: 'Keyboard shortcut action: toggle the order type between Market and Limit',
      message: 'Market / Limit',
    }),
  },
  {
    id: 'closePosition',
    key: 'c',
    tradeOnly: true,
    label: msg({
      comment: "Keyboard shortcut action: open the close dialog for the selected market's position",
      context: 'keyboard shortcut',
      message: 'Close Position',
    }),
  },
  { id: 'openSettings', key: '.', label: msg({ comment: 'Keyboard shortcut action', message: 'Open Settings' }) },
  { id: 'deposit', key: 'd', label: msg({ comment: 'Keyboard shortcut action', message: 'Deposit' }) },
  { id: 'withdraw', key: 'w', label: msg({ comment: 'Keyboard shortcut action', message: 'Withdraw' }) },
];

// Checkbox, radio and range inputs back MUI Switch/Checkbox, Rating and Slider: focused there isn't typing.
const EDITABLE_SELECTOR =
  'input:not([type="checkbox"]):not([type="radio"]):not([type="range"]), textarea, select, [contenteditable=""], [contenteditable="true"]';

/**
 * True when the key went to a field. Reads `composedPath()[0]`: inside a shadow root (the PostHog survey),
 * `event.target` is retargeted to the shadow host and would miss the field.
 */
export function isTypingTarget(event: Pick<Event, 'composedPath' | 'target'>): boolean {
  const target = event.composedPath()[0] ?? event.target;
  return target instanceof HTMLElement && (target.isContentEditable || target.closest(EDITABLE_SELECTOR) !== null);
}

/**
 * True when a modal surface owns the keyboard: an MUI dialog, drawer or popover menu,
 * or a third-party modal marked `aria-modal` (RainbowKit). `ignore` excepts MUI modals matching that selector.
 */
export function isModalOpen(ignore?: string): boolean {
  if (document.querySelector(`.MuiModal-root:not(.MuiModal-hidden)${ignore ? `:not(${ignore})` : ''}`)) return true;
  // MUI papers keep `aria-modal` while closed under `keepMounted` (the mobile drawer); MUI is covered above.
  // funkit hides its modal with display: none and keeps `aria-modal`. No checkVisibility (Safari < 17.4): assume shown.
  return Array.from(document.querySelectorAll('[aria-modal="true"]')).some(
    (el) => !el.closest('.MuiModal-root') && (el.checkVisibility?.() ?? true)
  );
}

export function matchShortcut(event: KeyboardEvent, isMac = IS_MAC): ShortcutDefinition | undefined {
  if (event.repeat) return undefined;
  return GENERAL_SHORTCUTS.find((shortcut) => isShortcut(event, shortcut, isMac));
}
